import React, { useState, useEffect } from 'react';
import { FaHandshake } from "react-icons/fa";
import { FaHandHoldingUsd } from "react-icons/fa";
import MainLayout from '../components/layout/MainLayout';
import {
  hasModule,
  canViewModule,
  isConcesionario,
  isHonda,
  isXerox,
  getGroupDisplayName,
  getUserGroupId,
  GROUP_TYPES
} from '../utils/auth';

const Home = ({ user, onLogout, onNavigate }) => {
  const [modules, setModules] = useState([]);
  const [greeting, setGreeting] = useState('');
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedModuleName, setBlockedModuleName] = useState('');
  const [userGroupName, setUserGroupName] = useState('');

  // Función para obtener el saludo según la hora
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return '¡Buenos días';
    } else if (hour >= 12 && hour < 19) {
      return '¡Buenas tardes';
    } else {
      return '¡Buenas noches';
    }
  };

  useEffect(() => {
    setGreeting(getGreeting());
    const groupDisplayName = getGroupDisplayName();
    setUserGroupName(groupDisplayName);

    // Construir array de módulos basado en los permisos del usuario
    let modulesArray = [];

    // Módulo de Cesiones - Verificar si tiene el módulo CESIONES
    // Xerox siempre puede verlo aunque no tenga el módulo explícito
    if (canViewModule('CESIONES')) {
      modulesArray.push({
        id: 2,
        title: 'Cesiones',
        icon: FaHandshake,
        description: 'Transferencias de vehículos',
        action: 'cesionesDashboard'
      });
    }

    // Módulo de Prendas - Verificar si tiene el módulo PRENDAS
    if (canViewModule('PRENDAS')) {
      modulesArray.push({
        id: 3,
        title: 'Prendas',
        icon: FaHandHoldingUsd,
        description: 'Créditos prendarios',
        action: 'prendasDashboard',
      });
    }

    // Módulo de Scoring - Verificar si tiene el módulo SCORING
    // Diferentes descripciones según el grupo
    if (canViewModule('SCORING')) {
      let scoringDescription = 'Administre el proceso de scoring crediticio';

      if (isConcesionario()) {
        scoringDescription = 'Gestión de scoring para concesionarios';
      } else if (isHonda()) {
        scoringDescription = 'Gestión completa de scoring Honda';
      } else if (isXerox()) {
        scoringDescription = 'Administración total del sistema de scoring';
      }

      modulesArray.push({
        id: 6,
        title: 'Scoring',
        icon: 'analytics',
        description: scoringDescription,
        action: 'scoringDashboard'
      });
    }

    // Módulo de ABM Usuarios - Verificar si tiene el módulo ABM
    // Solo Xerox y usuarios con módulo ABM pueden verlo
    if (canViewModule('ABM')) {
      modulesArray.push({
        id: 8,
        title: 'ABM Usuarios',
        icon: 'group',
        description: 'Administre los usuarios del sistema',
        action: 'abmUsuarios'
      });
    }

    setModules(modulesArray);
  }, []); // Solo ejecutar una vez al montar el componente

  const handleModuleClick = (module) => {
    if (module.blocked) {
      setBlockedModuleName(module.title);
      setShowBlockedModal(true);
      return;
    }
    if (module.action) {
      onNavigate(module.action);
    } else {
      setBlockedModuleName(module.title);
      setShowBlockedModal(true);
    }
  };

  // Texto de bienvenida 
  const getWelcomeMessage = () => {
    const welcomeText =
      'Bienvenido a la Plataforma Integral de Gestión Digital. Desde aquí podrá acceder de forma centralizada y segura a las herramientas y funcionalidades disponibles dentro del sistema.';

    const groupId = getUserGroupId();

    if (
      groupId === GROUP_TYPES.XEROX ||
      groupId === GROUP_TYPES.HONDA ||
      groupId === GROUP_TYPES.CONCESIONARIO
    ) {
      return welcomeText;
    }

    // Si no tiene grupo definido
    if (hasModule('ABM')) {
      return welcomeText;
    }

    return welcomeText;
  };

  // Determinar las clases de grid según la cantidad de módulos
  // Siempre centrado, adaptándose a la cantidad de módulos
  const getGridClasses = () => {
    const moduleCount = modules.length;

    if (moduleCount === 1) {
      // 1 módulo: centrado, ancho máximo de 1 card
      return "grid grid-cols-1 gap-6 max-w-md mx-auto";
    } else if (moduleCount === 2) {
      // 2 módulos: centrados, máximo 2 columnas
      return "grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto";
    } else if (moduleCount === 3) {
      // 3 módulos: centrados, máximo 3 columnas
      return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto";
    } else if (moduleCount === 4) {
      // 4 módulos: centrados, máximo 4 columnas
      return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto";
    } else {
      // 5+ módulos: distribuidos en grid responsivo
      return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto";
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-2 xl:px-3 py-3 xl:py-4 animate-fade-in-up">

        {/* Saludo de bienvenida mejorado - Optimizado para 1366x768 */}
        <div className="mb-6 xl:mb-12 text-center">
          <h2 className="text-2xl md:text-3xl xl:text-4xl font-bold text-gradient mb-2 xl:mb-3">
            {greeting}, {user?.name || user?.username || 'Usuario'}!
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-xs xl:text-sm leading-relaxed">
            {getWelcomeMessage()}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2 xl:mt-4">
            <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-honda-400 rounded-full"></div>
            <div className="w-16 h-1 bg-gradient-to-r from-honda-500 to-honda-600 rounded-full shadow-glow-red"></div>
            <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-honda-400 rounded-full"></div>
          </div>
        </div>

        {/* Grid responsive con cards mejoradas */}
        <div className={getGridClasses()}>
          {modules.map((module, index) => (
            <div
              key={module.id}
              onClick={() => handleModuleClick(module)}
              className="group cursor-pointer animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative bg-white border-2 border-gray-100 rounded-xl xl:rounded-2xl shadow-soft hover:shadow-xl transition-all duration-500 hover:border-honda-200 hover:-translate-y-2 hover:shadow-glow-red overflow-hidden h-full">
                {/* Barra superior con gradiente */}
                <div className="h-1 xl:h-1.5 gradient-red group-hover:h-2 transition-all duration-300"></div>

                {/* Efecto de brillo en hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-honda-50/0 via-honda-50/50 to-honda-50/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative p-5 xl:p-8 text-center">
                  {/* Ícono con animación - Optimizado para 1366x768 */}
                  <div className="mb-3 xl:mb-5 flex justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-honda-100 rounded-xl xl:rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="relative w-16 h-16 xl:w-20 xl:h-20 bg-gradient-to-br from-honda-50 to-honda-100 rounded-xl xl:rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-soft">
                        {typeof module.icon === 'string' ? (
                          <span className="material-icons text-3xl xl:text-4xl text-honda-600 group-hover:scale-110 transition-transform duration-500">
                            {module.icon}
                          </span>
                        ) : (
                          <module.icon className="text-3xl xl:text-4xl text-honda-600 group-hover:scale-110 transition-transform duration-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Título - Optimizado para 1366x768 */}
                  <h3 className="text-lg xl:text-xl font-bold text-gray-800 mb-2 xl:mb-3 group-hover:text-honda-700 transition-colors duration-300">
                    {module.title}
                  </h3>

                  {/* Descripción - Optimizado para 1366x768 */}
                  {module.description && (
                    <p className="text-xs xl:text-sm text-gray-500 line-clamp-2 mb-3 xl:mb-4 leading-relaxed">
                      {module.description}
                    </p>
                  )}

                  {/* Call to action con animación - Optimizado para 1366x768 */}
                  <div className="mt-3 xl:mt-5 pt-2 xl:pt-3 border-t border-gray-100 invisible group-hover:visible transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    <span className="inline-flex items-center gap-2 text-honda-600 text-xs xl:text-sm font-semibold">
                      Acceder al módulo
                      <span className="material-icons text-lg group-hover:translate-x-1 transition-transform duration-300">arrow_forward</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal módulo en desarrollo */}
        {showBlockedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowBlockedModal(false)}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="material-icons text-white text-2xl">info</span>
                  </div>
                  <h2 className="text-white text-xl font-bold">Módulo en desarrollo</h2>
                </div>
                <button
                  onClick={() => setShowBlockedModal(false)}
                  className="text-white/80 hover:text-white transition-colors w-9 h-9 flex items-center justify-center rounded-xl hover:bg-white/10"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <span className="material-icons text-amber-500 text-3xl flex-shrink-0">construction</span>
                  <div>
                    <p className="text-gray-800 font-semibold mb-1">Módulo temporalmente no disponible</p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      El módulo de <span className="font-semibold text-red-600">{blockedModuleName}</span> se encuentra actualmente en desarrollo.
                      Estará disponible próximamente. Para más información comuníquese con su administrador.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBlockedModal(false)}
                  className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Estado vacío mejorado */}
        {modules.length === 0 && (
          <div className="text-center py-20 animate-fade-in">
            <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
              <span className="material-icons text-6xl text-gray-300">dashboard</span>
            </div>
            <p className="text-gray-400 text-lg">No hay módulos disponibles</p>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default Home;
